

import api from "../api"

export async function getProducts(organizationApikey) {
  if (this.getState().productsLoading) return

  this.setState({
    productsLoading: true,
    productsError: undefined,
  })

  try {
    const products = await api.getProducts({ organizationApikey })
    this.setState({ products })
  } catch (productsError) {
    this.setState({ productsError })
  } finally {
    this.setState({ productsLoading: undefined })
  }
}

export async function getProductById(productId) {
  if (this.getState().loadingProduct) return

  this.setState({
    loadingProduct: true,
    productError: undefined,
  })

  try {
    const product = await api.getProductById({ productId })

    const currentProducts = this.getState().products || []
    const updatedProducts = currentProducts.map(p => p.id === productId ? product : p)

    if (!currentProducts.find(p => p.id === productId)) {
      updatedProducts.push(product)
    }

    this.setState({ products: updatedProducts })
    return product
  } catch (productError) {
    this.setState({ productError })
  } finally {
    this.setState({ loadingProduct: undefined })
  }
}

export async function createProduct(organizationApikey, productData) {
  if (this.getState().creatingProduct) return

  this.setState({
    creatingProduct: true,
    createProductError: undefined,
  })

  try {
    const product = await api.createProduct({
      organizationApikey,
      productData
    })

    const currentProducts = this.getState().products || []

    const newProduct = await api.getProductById({
      productId: product.id
    })

    this.setState({
      products: [newProduct, ...currentProducts],
      lastCreatedProduct: newProduct
    })

    return product.id
  } catch (createProductError) {
    this.setState({ createProductError })
    throw createProductError
  } finally {
    this.setState({ creatingProduct: false })
  }
}

export async function updateProduct(organizationApikey, productId, productData) {
  if (this.getState().updatingProduct) return

  this.setState({
    updatingProduct: true,
    updateProductError: undefined,
  })

  try {
    const result = await api.updateProduct({
      organizationApikey,
      productId,
      productData
    })

    const currentProducts = this.getState().products || []

    const updatedProduct = await api.getProductById({
      organizationApikey,
      productId
    })

    const updatedProducts = currentProducts.map(product =>
      product.id === productId ? updatedProduct : product
    )

    this.setState({ products: updatedProducts })
    return result.productId
  } catch (updateProductError) {
    this.setState({ updateProductError })
    throw updateProductError
  } finally {
    this.setState({ updatingProduct: false })
  }
}

export async function deleteProduct(organizationApikey, productId) {
  if (this.getState().deletingProduct) return

  this.setState({
    deletingProduct: true,
    deleteProductError: undefined,
  })

  try {
    await api.deleteProduct({
      organizationApikey,
      productId
    })

    const currentProducts = this.getState().products || []
    const updatedProducts = currentProducts.filter(product => product.id !== productId)

    this.setState({ products: updatedProducts })
    return true
  } catch (deleteProductError) {
    this.setState({ deleteProductError })
    throw deleteProductError
  } finally {
    this.setState({ deletingProduct: false })
  }
}

export async function reorderProducts(organizationApikey, productOrders) {
  if (this.getState().reorderingProducts) return

  this.setState({
    reorderingProducts: true,
    reorderProductsError: undefined,
  })

  try {
    await api.reorderProducts({
      organizationApikey,
      productOrders
    })

    // Update local products with new display orders
    const currentProducts = this.getState().products || []
    const updatedProducts = currentProducts.map(product => {
      const orderData = productOrders.find(po => po.productId === product.id)
      if (orderData) {
        return { ...product, display_order: orderData.displayOrder }
      }
      return product
    })

    // Sort by display_order
    updatedProducts.sort((a, b) => (a.display_order || 0) - (b.display_order || 0))

    this.setState({ products: updatedProducts })
    return true
  } catch (reorderProductsError) {
    this.setState({ reorderProductsError })
    throw reorderProductsError
  } finally {
    this.setState({ reorderingProducts: false })
  }
}

export async function updateVariantInventory(organizationApikey, productId, variantId, inventoryQuantity) {
  if (this.getState().updatingInventory) return

  this.setState({
    updatingInventory: true,
    updateInventoryError: undefined,
  })

  try {
    const variant = await api.updateVariantInventory({
      organizationApikey,
      productId,
      variantId,
      inventoryQuantity
    })

    const currentProducts = this.getState().products || []
    const updatedProducts = currentProducts.map(product => {
      if (product.id === productId) {
        return {
          ...product,
          variants: product.variants.map(v =>
            v.id === variantId ? { ...v, inventory_quantity: inventoryQuantity, updated_at: new Date() } : v
          )
        }
      }
      return product
    })

    this.setState({ products: updatedProducts })
    return variant
  } catch (updateInventoryError) {
    this.setState({ updateInventoryError })
    throw updateInventoryError
  } finally {
    this.setState({ updatingInventory: false })
  }
}

export async function getProductCollections(organizationApikey) {
  if (this.getState().collectionsLoading) return

  this.setState({
    collectionsLoading: true,
    collectionsError: undefined,
  })

  try {
    const collections = await api.getProductCollections({ organizationApikey })
    this.setState({ productCollections: collections })
  } catch (collectionsError) {
    this.setState({ collectionsError })
  } finally {
    this.setState({ collectionsLoading: undefined })
  }
}

export async function createProductCollection(organizationApikey, collectionData) {
  if (this.getState().creatingCollection) return

  this.setState({
    creatingCollection: true,
    createCollectionError: undefined,
  })

  try {
    const collection = await api.createProductCollection({
      organizationApikey,
      title: collectionData.title,
      handle: collectionData.handle
    })

    const currentCollections = this.getState().productCollections || []
    this.setState({
      productCollections: [...currentCollections, collection]
    })

    return collection
  } catch (createCollectionError) {
    this.setState({ createCollectionError })
    throw createCollectionError
  } finally {
    this.setState({ creatingCollection: undefined })
  }
}

export async function getProductTypes(organizationApikey) {
  if (this.getState().typesLoading) return

  this.setState({
    typesLoading: true,
    typesError: undefined,
  })

  try {
    const types = await api.getProductTypes({ organizationApikey })
    this.setState({ productTypes: types })
  } catch (typesError) {
    this.setState({ typesError })
  } finally {
    this.setState({ typesLoading: undefined })
  }
}

export async function createProductType(organizationApikey, typeData) {
  if (this.getState().creatingType) return

  this.setState({
    creatingType: true,
    createTypeError: undefined,
  })

  try {
    const type = await api.createProductType({
      organizationApikey,
      value: typeData.value
    })

    const currentTypes = this.getState().productTypes || []
    this.setState({
      productTypes: [...currentTypes, type]
    })

    return type
  } catch (createTypeError) {
    this.setState({ createTypeError })
    throw createTypeError
  } finally {
    this.setState({ creatingType: undefined })
  }
}


export async function getProductsByCollection(organizationApikey, collectionId) {
  if (this.getState().loadingCollectionProducts) return

  this.setState({
    loadingCollectionProducts: true,
    collectionProductsError: undefined,
  })

  try {
    const products = await api.getProductsByCollection({ organizationApikey, collectionId })

    const currentProducts = this.getState().products || []
    const updatedProducts = [...currentProducts]

    products.forEach(product => {
      const existingIndex = updatedProducts.findIndex(p => p.id === product.id)
      if (existingIndex >= 0) {
        updatedProducts[existingIndex] = product
      } else {
        updatedProducts.push(product)
      }
    })

    this.setState({
      products: updatedProducts,
      collectionProducts: products
    })

    return { products }
  } catch (collectionProductsError) {
    this.setState({ collectionProductsError })
    throw collectionProductsError
  } finally {
    this.setState({ loadingCollectionProducts: undefined })
  }
}

export async function getProductsByType(organizationApikey, typeId) {
  if (this.getState().loadingTypeProducts) return

  this.setState({
    loadingTypeProducts: true,
    typeProductsError: undefined,
  })

  try {
    const products = await api.getProductsByType({ organizationApikey, typeId })

    this.setState({ typeProducts: products })
    return { products }
  } catch (typeProductsError) {
    this.setState({ typeProductsError })
    throw typeProductsError
  } finally {
    this.setState({ loadingTypeProducts: undefined })
  }
}

export async function loadShoppingCart({ organizationApikey }) {
  if (this.getState().shoppingCartItemsLoading || this.getState().shoppingCartTotalsLoading) return
  this.setState({
    shoppingCartItemsLoading: true,
    shoppingCartItemsError: undefined,
    shoppingCartTotalsLoading: true,
    shoppingCartTotalsError: undefined,
  })
  try {
    const {items, totals} = await api.getShoppingCartItems({ organizationApikey })
    this.setState({ shoppingCartItems: items, shoppingCartTotals: totals })
  } catch (error) {
    this.setState({ shoppingCartItemsError: error, shoppingCartTotalsError: error })
  } finally {
    this.setState({ shoppingCartItemsLoading: undefined, shoppingCartTotalsLoading: undefined })
  }
}

export async function addCartItem({ organizationApikey, productId, variantId, quantity }) {
  if (this.getState().addingCartItem) return
  this.setState({
    addingCartItem: true,
    addCartItemError: undefined,
  })
  try {
    const shoppingCartItems = await api.addCartItem({
      organizationApikey,
      productId,
      variantId,
      quantity
    })

    this.setState({
      shoppingCartItems,
    })

    await this.takeAction('marketplace.loadShoppingCart', { organizationApikey })

    return shoppingCartItems
  } catch (addCartItemError) {
    this.setState({ addCartItemError })
    throw addCartItemError
  } finally {
    this.setState({ addingCartItem: undefined })
  }
}

export async function updateCartItemQuantity({ productId, variantId, quantity }) {
  if (this.getState().updatingCartItem) return

  this.setState({
    updatingCartItem: true,
    updateCartItemError: undefined,
  })

  try {
    const updatedItem = await api.updateCartItemQuantity({ productId, variantId, quantity })

    const currentCartItems = this.getState().shoppingCartItems || []
    const updatedCartItems = currentCartItems.map(item =>
      (item.product_id === productId &&
       (variantId ? item.variant_id === variantId : !item.variant_id)) ? updatedItem : item
    )

    this.setState({ shoppingCartItems: updatedCartItems })

    return updatedItem
  } catch (updateCartItemError) {
    this.setState({ updateCartItemError })
    throw updateCartItemError
  } finally {
    this.setState({ updatingCartItem: undefined })
  }
}

export async function removeCartItem({ productId, variantId, organizationApikey }) {
  if (this.getState().removingCartItem) return
  this.setState({
    removingCartItem: true,
    removeCartItemError: undefined,
  })

  try {
    await api.removeCartItem({ productId, variantId, organizationApikey })

    const currentCartItems = this.getState().shoppingCartItems || []
    const updatedCartItems = currentCartItems.filter(item =>
      !(item.product_id === productId &&
        (variantId ? item.variant_id === variantId : !item.variant_id))
    )

    this.setState({ shoppingCartItems: updatedCartItems })
  } catch (removeCartItemError) {
    this.setState({ removeCartItemError })
    throw removeCartItemError
  } finally {
    this.setState({ removingCartItem: undefined })
  }
}

export async function clearCart({ organizationApikey } = {}) {
  if (this.getState().clearingCart) return

  this.setState({
    clearingCart: true,
    clearCartError: undefined,
  })

  try {
    await api.clearCart({ organizationApikey })

    if (organizationApikey) {
      const currentCartItems = this.getState().shoppingCartItems || []
      const updatedCartItems = currentCartItems.filter(item =>
        item.organization_apikey !== organizationApikey
      )
      this.setState({ shoppingCartItems: updatedCartItems })
    } else {
      this.setState({ shoppingCartItems: [] })
    }
  } catch (clearCartError) {
    this.setState({ clearCartError })
    throw clearCartError
  } finally {
    this.setState({ clearingCart: undefined })
  }
}

export async function getOrder({ orderNumber }) {
  if (this.getState().loadingOrder) return

  this.setState({
    loadingOrder: true,
    orderError: undefined,
  })

  try {
    const order = await api.getOrder({ orderNumber })

    const currentOrders = this.getState().orders || []
    const updatedOrders = currentOrders.map(o =>
      o.order_number === orderNumber ? order : o
    )

    if (!currentOrders.find(o => o.order_number === orderNumber)) {
      updatedOrders.push(order)
    }

    this.setState({
      orders: updatedOrders,
      currentOrder: order
    })

    return order
  } catch (orderError) {
    this.setState({ orderError })
    throw orderError
  } finally {
    this.setState({ loadingOrder: undefined })
  }
}

export async function getOrderTracking({ orderNumber }) {
  if (this.getState().loadingOrderTracking) return

  this.setState({
    loadingOrderTracking: true,
    orderTrackingError: undefined,
  })

  try {
    const tracking = await api.getOrderTracking({ orderNumber })

    console.log('tracking', tracking)

    this.setState({
      orderTracking: tracking,
      orderTrackingError: undefined
    })

    return tracking
  } catch (orderTrackingError) {
    this.setState({ orderTrackingError })
    return []
  } finally {
    this.setState({ loadingOrderTracking: undefined })
  }
}

export async function loadUserOrders({ limit, offset } = {}) {
  if (this.getState().loadingOrders) return

  this.setState({
    loadingOrders: true,
    ordersError: undefined,
  })

  try {
    const orders = await api.getUserOrders({ limit, offset })

    this.setState({
      orders,
      ordersError: undefined
    })

    return orders
  } catch (ordersError) {
    this.setState({ ordersError })
    throw ordersError
  } finally {
    this.setState({ loadingOrders: undefined })
  }
}

export async function updateOrderStatus({
  userDid,
  orderNumber,
  orderStatus,
  fulfillmentStatus,
  description,
  trackingNumber,
  carrier,
  internalNotes
}) {
  if (this.getState().loadingOrderUpdate) return

  this.setState({
    loadingOrderUpdate: true,
    orderUpdateError: undefined,
  })

  try {
    const updatedOrder = await api.updateOrderStatus({
      userDid,
      orderNumber,
      orderStatus,
      fulfillmentStatus,
      description,
      trackingNumber,
      carrier,
      internalNotes
    })

    const currentOrders = this.getState().orders || []
    const updatedOrders = currentOrders.map(o =>
      o.order_number === orderNumber ? updatedOrder : o
    )

    this.setState({
      orders: updatedOrders,
      currentOrder: updatedOrder,
      orderUpdateError: undefined
    })

    await this.takeAction('marketplace.getOrderTracking', { orderNumber })

    return updatedOrder
  } catch (orderUpdateError) {
    this.setState({ orderUpdateError })
    throw orderUpdateError
  } finally {
    this.setState({ loadingOrderUpdate: undefined })
  }
}

export async function createCheckoutSession(checkoutData) {
  if (this.getState().loadingCheckout) return

  const { selectedItems, shippingAddress, billingAddress, customerNotes } = checkoutData

  if (!selectedItems || selectedItems.length === 0) {
    throw new Error('No items selected for checkout')
  }

  this.setState({
    loadingCheckout: true,
    checkoutError: undefined,
  })

  try {
    const session = await api.createCheckoutSession({
      selectedItems,
      shippingAddress,
      billingAddress,
      customerNotes
    })

    this.setState({
      checkoutSession: session,
      checkoutError: undefined
    })

    return session
  } catch (checkoutError) {
    this.setState({ checkoutError })
    throw checkoutError
  } finally {
    this.setState({ loadingCheckout: undefined })
  }
}

export async function loadSellerOrders({ limit, offset } = {}) {
  if (this.getState().loadingSellerOrders) return

  this.setState({
    loadingSellerOrders: true,
    sellerOrdersError: undefined,
  })

  try {
    const sellerOrders = await api.getSellerOrders({ limit, offset })

    this.setState({
      sellerOrders,
      sellerOrdersError: undefined
    })

    return sellerOrders
  } catch (sellerOrdersError) {
    this.setState({ sellerOrdersError })
    throw sellerOrdersError
  } finally {
    this.setState({ loadingSellerOrders: undefined })
  }
}

export async function updateSellerOrderStatus({
  orderNumber,
  orderStatus,
  fulfillmentStatus,
  description,
  trackingNumber,
  carrier,
  internalNotes,
  reversionReason,
  customerNotificationMessage,
  sendCustomerNotification = true,
  itemFulfillments
}) {
  if (this.getState().updatingSellerOrderStatus) return

  this.setState({
    updatingSellerOrderStatus: true,
    sellerOrderUpdateError: undefined,
  })

  try {
    const updatedOrder = await api.updateOrderStatus({
      orderNumber,
      orderStatus,
      fulfillmentStatus,
      description,
      trackingNumber,
      carrier,
      internalNotes,
      reversionReason,
      customerNotificationMessage,
      sendCustomerNotification,
      itemFulfillments
    })

    const currentSellerOrders = this.getState().sellerOrders || []
    const updatedSellerOrders = currentSellerOrders.map(order =>
      order.order_number === orderNumber ? { ...order, ...updatedOrder } : order
    )

    this.setState({
      sellerOrders: updatedSellerOrders,
      sellerOrderUpdateError: undefined
    })

    return updatedOrder
  } catch (sellerOrderUpdateError) {
    this.setState({ sellerOrderUpdateError })
    throw sellerOrderUpdateError
  } finally {
    this.setState({ updatingSellerOrderStatus: undefined })
  }
}


export async function getOrderAuditTrail(orderId, { limit = 50, includeArchived = false } = {}) {
  const orderAuditKey = `order:${orderId}:audit`
  const loadingKey = `${orderAuditKey}:trail:loading`
  const errorKey = `${orderAuditKey}:trail:error`
  const dataKey = `${orderAuditKey}:trail`
  const loadedKey = `${orderAuditKey}:trail:loaded`

  if (this.getState()[loadingKey]) return

  console.log('getting audit trail', orderId)

  this.setState({
    [loadingKey]: true,
    [errorKey]: undefined,
  })

  try {
    const auditTrail = await api.getOrderAuditTrail({
      orderNumber: orderId,
      limit,
      includeArchived
    })

    this.setState({
      [dataKey]: auditTrail || [],
      [errorKey]: undefined,
      [loadedKey]: true
    })

    return auditTrail || []
  } catch (error) {
    this.setState({
      [errorKey]: error,
      [loadedKey]: false
    })
    throw error
  } finally {
    this.setState({ [loadingKey]: undefined })
  }
}

export async function getOrderNotes(orderId, { noteType = null, limit = 20 } = {}) {
  const orderAuditKey = `order:${orderId}:audit`
  const loadingKey = `${orderAuditKey}:notes:loading`
  const errorKey = `${orderAuditKey}:notes:error`
  const dataKey = `${orderAuditKey}:notes`
  const loadedKey = `${orderAuditKey}:notes:loaded`

  if (this.getState()[loadingKey]) return

  this.setState({
    [loadingKey]: true,
    [errorKey]: undefined,
  })

  try {
    const orderNotes = await api.getOrderNotes({
      orderNumber: orderId,
      noteType,
      limit
    })

    this.setState({
      [dataKey]: orderNotes || [],
      [errorKey]: undefined,
      [loadedKey]: true
    })

    return orderNotes || []
  } catch (error) {
    this.setState({
      [errorKey]: error,
      [loadedKey]: false
    })
    throw error
  } finally {
    this.setState({ [loadingKey]: undefined })
  }
}

export async function getOrderStatusHistory(orderId, { limit = 10 } = {}) {
  const orderAuditKey = `order:${orderId}:audit`
  const loadingKey = `${orderAuditKey}:statusHistory:loading`
  const errorKey = `${orderAuditKey}:statusHistory:error`
  const dataKey = `${orderAuditKey}:statusHistory`
  const loadedKey = `${orderAuditKey}:statusHistory:loaded`

  if (this.getState()[loadingKey]) return

  this.setState({
    [loadingKey]: true,
    [errorKey]: undefined,
  })

  try {
    const statusHistory = await api.getOrderStatusHistory({
      orderNumber: orderId,
      limit
    })

    this.setState({
      [dataKey]: statusHistory || [],
      [errorKey]: undefined,
      [loadedKey]: true
    })

    return statusHistory || []
  } catch (error) {
    this.setState({
      [errorKey]: error,
      [loadedKey]: false
    })
    throw error
  } finally {
    this.setState({ [loadingKey]: undefined })
  }
}

export async function getRecentReversions(orderId, { days = 7, limit = 50 } = {}) {
  const orderAuditKey = `order:${orderId}:audit`
  const loadingKey = `${orderAuditKey}:recentReversions:loading`
  const errorKey = `${orderAuditKey}:recentReversions:error`
  const dataKey = `${orderAuditKey}:recentReversions`
  const loadedKey = `${orderAuditKey}:recentReversions:loaded`

  if (this.getState()[loadingKey]) return

  this.setState({
    [loadingKey]: true,
    [errorKey]: undefined,
  })

  try {
    const recentReversions = await api.getRecentReversions({
      orderNumber: orderId,
      days,
      limit
    })

    this.setState({
      [dataKey]: recentReversions || [],
      [errorKey]: undefined,
      [loadedKey]: true
    })

    return recentReversions || []
  } catch (error) {
    this.setState({
      [errorKey]: error,
      [loadedKey]: false
    })
    throw error
  } finally {
    this.setState({ [loadingKey]: undefined })
  }
}

export async function getOrderNotificationStatus(orderId) {
  const orderAuditKey = `order:${orderId}:audit`
  const loadingKey = `${orderAuditKey}:notificationStatus:loading`
  const errorKey = `${orderAuditKey}:notificationStatus:error`
  const dataKey = `${orderAuditKey}:notificationStatus`
  const loadedKey = `${orderAuditKey}:notificationStatus:loaded`

  if (this.getState()[loadingKey]) return

  this.setState({
    [loadingKey]: true,
    [errorKey]: undefined,
  })

  try {
    const notificationStatus = await api.getOrderNotificationStatus({
      orderNumber: orderId
    })

    this.setState({
      [dataKey]: notificationStatus || [],
      [errorKey]: undefined,
      [loadedKey]: true
    })

    return notificationStatus || []
  } catch (error) {
    this.setState({
      [errorKey]: error,
      [loadedKey]: false
    })
    throw error
  } finally {
    this.setState({ [loadingKey]: undefined })
  }
}

export async function searchOrdersByAudit({
  statusFrom = null,
  statusTo = null,
  isReversion = null,
  updatedBy = null,
  dateFrom = null,
  dateTo = null,
  limit = 100
} = {}) {
  const loadingKey = 'auditSearch:searching'
  const errorKey = 'auditSearch:error'
  const dataKey = 'auditSearch:results'

  if (this.getState()[loadingKey]) return

  this.setState({
    [loadingKey]: true,
    [errorKey]: undefined,
  })

  try {
    const orders = await api.searchOrdersByAudit({
      statusFrom,
      statusTo,
      isReversion,
      updatedBy,
      dateFrom,
      dateTo,
      limit
    })

    this.setState({
      [dataKey]: orders || [],
      [errorKey]: undefined
    })

    return orders || []
  } catch (error) {
    this.setState({ [errorKey]: error })
    throw error
  } finally {
    this.setState({ [loadingKey]: undefined })
  }
}

export async function getAuditSummary(orderId, { days = 30 } = {}) {
  const orderAuditKey = `order:${orderId}:audit`
  const loadingKey = `${orderAuditKey}:summary:loading`
  const errorKey = `${orderAuditKey}:summary:error`
  const dataKey = `${orderAuditKey}:summary`
  const loadedKey = `${orderAuditKey}:summary:loaded`

  if (this.getState()[loadingKey]) return

  this.setState({
    [loadingKey]: true,
    [errorKey]: undefined,
  })

  try {
    const auditSummary = await api.getAuditSummary({
      orderNumber: orderId,
      days
    })

    this.setState({
      [dataKey]: auditSummary,
      [errorKey]: undefined,
      [loadedKey]: true
    })

    return auditSummary
  } catch (error) {
    this.setState({
      [errorKey]: error,
      [loadedKey]: false
    })
    throw error
  } finally {
    this.setState({ [loadingKey]: undefined })
  }
}

export async function getAuditPerformanceMetrics(orderId) {
  const orderAuditKey = `order:${orderId}:audit`
  const loadingKey = `${orderAuditKey}:metrics:loading`
  const errorKey = `${orderAuditKey}:metrics:error`
  const dataKey = `${orderAuditKey}:metrics`
  const loadedKey = `${orderAuditKey}:metrics:loaded`

  if (this.getState()[loadingKey]) return

  this.setState({
    [loadingKey]: true,
    [errorKey]: undefined,
  })

  try {
    const performanceMetrics = await api.getAuditPerformanceMetrics({
      orderNumber: orderId
    })

    this.setState({
      [dataKey]: performanceMetrics,
      [errorKey]: undefined,
      [loadedKey]: true
    })

    return performanceMetrics
  } catch (error) {
    this.setState({
      [errorKey]: error,
      [loadedKey]: false
    })
    throw error
  } finally {
    this.setState({ [loadingKey]: undefined })
  }
}

export async function cancelOrder({ orderNumber, reason, description }) {
  if (this.getState().cancellingOrder) return

  this.setState({
    cancellingOrder: true,
    cancelOrderError: undefined,
  })

  try {
    const result = await api.cancelOrder({
      orderNumber,
      reason,
      description
    })

    const currentOrders = this.getState().orders || []
    const updatedOrders = currentOrders.map(order =>
      order.order_number === orderNumber
        ? { ...order, order_status: 'cancelled', cancellation_reason: reason, updated_at: new Date().toISOString() }
        : order
    )

    this.setState({
      orders: updatedOrders,
      cancelOrderError: undefined
    })

    return result
  } catch (cancelOrderError) {
    this.setState({ cancelOrderError })
    throw cancelOrderError
  } finally {
    this.setState({ cancellingOrder: undefined })
  }
}

export async function requestReturn({ orderNumber, reason, description, itemsToReturn }) {
  if (this.getState().requestingReturn) return

  this.setState({
    requestingReturn: true,
    requestReturnError: undefined,
  })

  try {
    const result = await api.requestReturn({
      orderNumber,
      reason,
      description,
      itemsToReturn
    })

    const currentOrders = this.getState().orders || []
    const updatedOrders = currentOrders.map(order =>
      order.order_number === orderNumber
        ? {
          ...order,
          return_request_status: 'pending',
          return_request_reason: reason,
          updated_at: new Date().toISOString()
        }
        : order
    )

    this.setState({
      orders: updatedOrders,
      requestReturnError: undefined
    })

    return result
  } catch (requestReturnError) {
    this.setState({ requestReturnError })
    throw requestReturnError
  } finally {
    this.setState({ requestingReturn: undefined })
  }
}

export async function getCustomerServiceConversations({ limit = 50, offset = 0 } = {}) {
  if (this.getState().loadingCustomerServiceConversations) return

  this.setState({
    loadingCustomerServiceConversations: true,
    customerServiceConversationsError: undefined,
  })

  try {
    const conversations = await api.getCustomerServiceConversations({ limit, offset })
    this.setState({
      customerServiceConversations: conversations,
      loadingCustomerServiceConversations: undefined
    })
    return conversations || []
  } catch (customerServiceConversationsError) {
    this.setState({ customerServiceConversationsError })
    throw customerServiceConversationsError
  } finally {
    this.setState({ loadingCustomerServiceConversations: undefined })
  }
}

export async function contactSellerAboutOrder({ orderNumber, message, subject }) {
  if (this.getState().contactingSeller) return

  this.setState({
    contactingSeller: true,
    contactSellerError: undefined,
  })

  try {
    const result = await api.contactSellerAboutOrder({
      orderNumber,
      message,
      subject
    })

    this.setState({
      contactSellerError: undefined
    })

    this.takeAction('marketplace.getCustomerServiceConversations')

    return result
  } catch (contactSellerError) {
    this.setState({ contactSellerError })
    throw contactSellerError
  } finally {
    this.setState({ contactingSeller: undefined })
  }
}

export async function getReturnRequests({ limit = 50, offset = 0, status = null } = {}) {
  if (this.getState().loadingReturnRequests) return

  this.setState({
    loadingReturnRequests: true,
    returnRequestsError: undefined,
  })

  try {
    const returnRequests = await api.getReturnRequests({ limit, offset, status })
    this.setState({
      returnRequests,
      loadingReturnRequests: undefined
    })
    return returnRequests || []
  } catch (returnRequestsError) {
    this.setState({ returnRequestsError })
    throw returnRequestsError
  } finally {
    this.setState({ loadingReturnRequests: undefined })
  }
}

export async function respondToReturnRequest({ returnRequestId, decision, adminNotes }) {
  if (this.getState().respondingToReturn) return

  this.setState({
    respondingToReturn: true,
    returnResponseError: undefined,
  })

  try {
    const result = await api.respondToReturnRequest({ returnRequestId, decision, adminNotes })

    const currentRequests = this.getState().returnRequests || []
    const updatedRequests = currentRequests.map(request =>
      request.id === returnRequestId ? { ...request, ...result } : request
    )

    this.setState({
      returnRequests: updatedRequests,
      respondingToReturn: undefined
    })
    return result
  } catch (returnResponseError) {
    this.setState({ returnResponseError })
    throw returnResponseError
  } finally {
    this.setState({ respondingToReturn: undefined })
  }
}

export async function getCustomerMessages({ limit = 50, offset = 0, status = null } = {}) {
  if (this.getState().loadingCustomerMessages) return

  this.setState({
    loadingCustomerMessages: true,
    customerMessagesError: undefined,
  })

  try {
    const customerMessages = await api.getCustomerMessages({ limit, offset, status })
    this.setState({
      customerMessages,
      loadingCustomerMessages: undefined
    })
    return customerMessages || []
  } catch (customerMessagesError) {
    this.setState({ customerMessagesError })
    throw customerMessagesError
  } finally {
    this.setState({ loadingCustomerMessages: undefined })
  }
}

export async function replyToCustomerMessage({ messageId, replyMessage }) {
  if (this.getState().replyingToMessage) return

  console.log('replyToCustomerMessage', { messageId, replyMessage })

  this.setState({
    replyingToMessage: true,
    messageReplyError: undefined,
  })

  try {
    const result = await api.replyToCustomerMessage({ messageId, message: replyMessage })

    const currentMessages = this.getState().customerMessages || []
    const updatedMessages = currentMessages.map(message =>
      message.id === messageId ? { ...message, ...result } : message
    )

    this.setState({
      customerMessages: updatedMessages,
      replyingToMessage: undefined
    })

    this.takeAction('marketplace.getCustomerServiceConversations')

    return result
  } catch (messageReplyError) {
    this.setState({ messageReplyError })
    throw messageReplyError
  } finally {
    this.setState({ replyingToMessage: undefined })
  }
}

